import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, Outlet, createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { Marca } from "@/components/medam/marca";
import { Button } from "@/components/ui/button";
import { clearAuthSession } from "@/lib/backend-auth";
import { estadoAdministrador } from "@/lib/administradores.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Panel administrativo | MEDAM" },
      { name: "description", content: "Gestión de posiciones y postulaciones de MEDAM." },
      { property: "og:title", content: "Panel administrativo | MEDAM" },
      { property: "og:description", content: "Área privada de gestión de MEDAM." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PanelAdmin,
});

const ENLACES = [
  { a: "/admin", etiqueta: "Resumen", exacto: true },
  { a: "/admin/posiciones", etiqueta: "Posiciones", exacto: false },
  { a: "/admin/postulaciones", etiqueta: "Postulaciones", exacto: false },
  { a: "/admin/equipo", etiqueta: "Equipo", exacto: false },
] as const;

function PanelAdmin() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const estado = useQuery({ queryKey: ["admin", "estado"], queryFn: () => estadoAdministrador() });

  useEffect(() => {
    const isPublicAuthRoute = location.pathname === "/acceso" || location.pathname === "/login";

    if (typeof window !== "undefined") {
      console.log("[admin guard] effect", {
        pathname: location.pathname,
        isPublicAuthRoute,
        isLoading: estado.isLoading,
        esAdmin: estado.data?.esAdmin,
        isError: estado.isError,
      });
    }

    if (estado.data && !estado.data.esAdmin && !isPublicAuthRoute) {
      if (typeof window !== "undefined") {
        console.log("[admin guard] no es admin; logout and redirect to /acceso", {
          pathname: location.pathname,
          esAdmin: estado.data.esAdmin,
        });
      }
      clearAuthSession();
      navigate({ to: "/acceso", replace: true });
    }
  }, [estado.data, estado.isError, estado.isLoading, location.pathname, navigate]);

  const cerrarSesion = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    clearAuthSession();
    navigate({ to: "/acceso", replace: true });
  };

  if (estado.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (estado.isError || (estado.data && !estado.data.esAdmin)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary/40 px-4">
        <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 text-center shadow-sm">
          <Marca className="justify-center" />
          <h1 className="mt-6 font-display text-xl">Verificando permisos</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {estado.error instanceof Error
              ? estado.error.message
              : "Tu sesión no está autorizada para este panel. Vuelve a iniciar sesión o solicita acceso."}
          </p>
          <Button variant="outline" className="mt-4 w-full" onClick={cerrarSesion}>
            Cerrar sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="border-b border-border/70 bg-card">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-4 sm:px-6 md:flex-row md:items-center md:justify-between">
          <Marca />
          <div className="flex flex-wrap items-center gap-2">
            <nav className="flex flex-wrap gap-1 text-sm">
              {ENLACES.map((enlace) => (
                <Link
                  key={enlace.a}
                  to={enlace.a}
                  activeOptions={{ exact: enlace.exacto }}
                  className="rounded-md px-3 py-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  activeProps={{ className: "bg-secondary text-foreground font-medium" }}
                >
                  {enlace.etiqueta}
                </Link>
              ))}
            </nav>
            <Button variant="outline" size="sm" onClick={cerrarSesion}>
              Cerrar sesión
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
